import type { UploadFile } from '@edwin-group/people-graphql';
import { Link, useNavigate } from 'react-router-dom';
import { useResponsiveImage } from '@/utils/useResponsiveImage';
import classNames from 'classnames';
import { CustomImage } from '@/components/CustomImage';
import MediaDuration from '@/components/MediaDuration';
import Button from '../Button';

type KnowledgeBaseVerticalCardProps = {
  title: string;
  description: string;
  thumbnail?: UploadFile;
  url: string;
  descriptionClasses?: string;
  duration?: string;
  onClick?: () => void;
  truncateDescription?: boolean;
  onClickButton?: () => void;
  noImage?: boolean;
  watchLabel?: string;
};

const KnowledgeBaseVerticalCard = ({
  title,
  description,
  thumbnail,
  url,
  noImage = false,
  descriptionClasses,
  duration,
  onClick,
  truncateDescription = true,
  onClickButton,
  watchLabel,
}: KnowledgeBaseVerticalCardProps) => {
  const navigate = useNavigate();
  const { src } = useResponsiveImage(thumbnail);

  const handleButtonClick = () => {
    if (onClickButton) {
      onClickButton();
    } else if (url) {
      navigate(url);
    }
  };

  return (
    <div className="flex flex-col gap-sh-16 rounded-sh-16 border border-people-gray-200 bg-white p-sh-16">
      {thumbnail && !noImage && (
        <div className=" w-full shrink-0 overflow-hidden rounded-t-sh-16 bg-people-green-200">
          <CustomImage
            src={src}
            alt={thumbnail.alternativeText || ''}
            className={classNames('h-full w-full bg-cover bg-center', onClick ? 'cursor-pointer' : '')}
            onClick={onClick}
          />
        </div>
      )}
      <div className="flex flex-1 flex-col gap-sh-16 pt-0">
        <div className="flex items-start justify-between gap-5">
          <h2 className="flex-1 font-haffer text-sh-20-30 font-bold text-black">{title}</h2>
        </div>
        <div>
          <p
            className={classNames(
              'mb-[8px] font-haffersq text-sh-18-28 text-black',
              descriptionClasses || '',
              truncateDescription ? 'line-clamp-2' : '',
            )}
            title={description}>
            {description}
          </p>
          {duration && <MediaDuration duration={duration} />}
        </div>
        <div className="flex items-center gap-3">
          <Button theme="outline-accent" onClick={handleButtonClick}>
            {watchLabel || 'Read now'}
          </Button>
          <Link to="/knowledge-base" className="font-haffer text-sh-12-18 font-bold !text-dark-green hover:underline">
            {'View all articles'}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default KnowledgeBaseVerticalCard;
